/**
 * Camera nodes — camera control, follow, shake, screen conversion.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// CAMERA POSITION & FOLLOW
// ============================================================================

export const SetCameraPosition: NodeBehaviour = {
  id: 'camera.setPosition',
  name: 'Set Camera Position',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Camera',
  description: 'Sets the camera position directly.',
  keywords: ['view', 'move', 'pan'],
  inputs: [
    flowIn(),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraPosition', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraPosition', ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const GetCameraPosition: NodeBehaviour = {
  id: 'camera.getPosition',
  name: 'Get Camera Position',
  category: 'Camera',
  isPure: true,
  color: '#e91e63',
  icon: 'Camera',
  description: 'Gets the current camera position.',
  keywords: ['view', 'position'],
  inputs: [],
  outputs: [
    output('pos', 'Position', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getCameraPosition', [], 'vec2');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:getCameraPosition') as { x: number; y: number } | null;
    ctx.setOutput('pos', r);
    ctx.setOutput('x', r?.x ?? 0);
    ctx.setOutput('y', r?.y ?? 0);
  },
};

export const SetCameraTarget: NodeBehaviour = {
  id: 'camera.setTarget',
  name: 'Set Camera Target',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Crosshair',
  description: 'Sets the entity for the camera to follow.',
  keywords: ['follow', 'track', 'focus'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraTarget', [ctx.resolveInput('entity')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraTarget', ctx.getInput<number>('entity') || ctx.entityId);
  },
};

export const ClearCameraTarget: NodeBehaviour = {
  id: 'camera.clearTarget',
  name: 'Clear Camera Target',
  category: 'Camera',
  color: '#e91e63',
  icon: 'X',
  description: 'Stops the camera from following any entity.',
  keywords: ['unfollow', 'stop', 'free'],
  inputs: [
    flowIn(),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:clearCameraTarget', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:clearCameraTarget');
  },
};

export const SetCameraFollowSmoothing: NodeBehaviour = {
  id: 'camera.setSmoothing',
  name: 'Set Camera Smoothing',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Sliders',
  description: 'Sets how smoothly the camera follows its target.',
  keywords: ['lag', 'lerp', 'damping'],
  inputs: [
    flowIn(),
    input('smoothing', 'Smoothing', 'f32', 5),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraSmoothing', [ctx.resolveInput('smoothing')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraSmoothing', ctx.getInput<number>('smoothing'));
  },
};

export const SetCameraDeadzone: NodeBehaviour = {
  id: 'camera.setDeadzone',
  name: 'Set Camera Deadzone',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Square',
  description: 'Sets the deadzone where the target can move without camera movement.',
  keywords: ['margin', 'tolerance', 'zone'],
  inputs: [
    flowIn(),
    input('width', 'Width', 'f32', 2),
    input('height', 'Height', 'f32', 2),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraDeadzone', [ctx.resolveInput('width'), ctx.resolveInput('height')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraDeadzone', ctx.getInput<number>('width'), ctx.getInput<number>('height'));
  },
};

export const SetCameraLookahead: NodeBehaviour = {
  id: 'camera.setLookahead',
  name: 'Set Camera Lookahead',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Eye',
  description: 'Sets how much the camera leads in the movement direction.',
  keywords: ['lead', 'anticipate', 'predict'],
  inputs: [
    flowIn(),
    input('distance', 'Distance', 'f32', 2),
    input('smoothing', 'Smoothing', 'f32', 5),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraLookahead', [ctx.resolveInput('distance'), ctx.resolveInput('smoothing')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraLookahead', ctx.getInput<number>('distance'), ctx.getInput<number>('smoothing'));
  },
};

// ============================================================================
// CAMERA ZOOM
// ============================================================================

export const SetCameraZoom: NodeBehaviour = {
  id: 'camera.setZoom',
  name: 'Set Camera Zoom',
  category: 'Camera',
  color: '#e91e63',
  icon: 'ZoomIn',
  description: 'Sets the camera zoom level.',
  keywords: ['scale', 'magnify', 'size'],
  inputs: [
    flowIn(),
    input('zoom', 'Zoom', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraZoom', [ctx.resolveInput('zoom')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraZoom', ctx.getInput<number>('zoom'));
  },
};

export const GetCameraZoom: NodeBehaviour = {
  id: 'camera.getZoom',
  name: 'Get Camera Zoom',
  category: 'Camera',
  isPure: true,
  color: '#e91e63',
  icon: 'ZoomIn',
  description: 'Gets the current camera zoom level.',
  keywords: ['scale', 'magnify'],
  inputs: [],
  outputs: [
    output('zoom', 'Zoom', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getCameraZoom', [], 'f32');
  },
  execute(ctx) {
    ctx.setOutput('zoom', ctx.bridge.call('bridge:getCameraZoom') as number ?? 1);
  },
};

export const SetCameraZoomLimits: NodeBehaviour = {
  id: 'camera.setZoomLimits',
  name: 'Set Zoom Limits',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Lock',
  description: 'Sets the min/max zoom levels.',
  keywords: ['clamp', 'range', 'bounds'],
  inputs: [
    flowIn(),
    input('min', 'Min Zoom', 'f32', 0.5),
    input('max', 'Max Zoom', 'f32', 3),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraZoomLimits', [ctx.resolveInput('min'), ctx.resolveInput('max')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraZoomLimits', ctx.getInput<number>('min'), ctx.getInput<number>('max'));
  },
};

// ============================================================================
// CAMERA BOUNDS
// ============================================================================

export const SetCameraBounds: NodeBehaviour = {
  id: 'camera.setBounds',
  name: 'Set Camera Bounds',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Maximize',
  description: 'Sets world bounds that the camera cannot leave.',
  keywords: ['limit', 'clamp', 'restrict'],
  inputs: [
    flowIn(),
    input('minX', 'Min X', 'f32', -100),
    input('minY', 'Min Y', 'f32', -100),
    input('maxX', 'Max X', 'f32', 100),
    input('maxY', 'Max Y', 'f32', 100),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCameraBounds', [
      ctx.resolveInput('minX'), ctx.resolveInput('minY'),
      ctx.resolveInput('maxX'), ctx.resolveInput('maxY'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setCameraBounds', ctx.getInput<number>('minX'), ctx.getInput<number>('minY'), ctx.getInput<number>('maxX'), ctx.getInput<number>('maxY'));
  },
};

export const ClearCameraBounds: NodeBehaviour = {
  id: 'camera.clearBounds',
  name: 'Clear Camera Bounds',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Unlock',
  description: 'Removes camera bounds restrictions.',
  keywords: ['unlimited', 'free'],
  inputs: [
    flowIn(),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:clearCameraBounds', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:clearCameraBounds');
  },
};

// ============================================================================
// CAMERA EFFECTS
// ============================================================================

export const CameraShake: NodeBehaviour = {
  id: 'camera.shake',
  name: 'Camera Shake',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Vibrate',
  description: 'Applies a shake effect to the camera.',
  keywords: ['trauma', 'rumble', 'impact', 'hit'],
  inputs: [
    flowIn(),
    input('intensity', 'Intensity', 'f32', 1),
    input('duration', 'Duration', 'f32', 0.3),
    input('frequency', 'Frequency', 'f32', 20),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:cameraShake', [
      ctx.resolveInput('intensity'),
      ctx.resolveInput('duration'),
      ctx.resolveInput('frequency'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:cameraShake', ctx.getInput<number>('intensity'), ctx.getInput<number>('duration'), ctx.getInput<number>('frequency'));
  },
};

export const AddCameraTrauma: NodeBehaviour = {
  id: 'camera.addTrauma',
  name: 'Add Camera Trauma',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Vibrate',
  description: 'Adds trauma to the camera (accumulative shake).',
  keywords: ['shake', 'impact', 'hit'],
  inputs: [
    flowIn(),
    input('trauma', 'Trauma', 'f32', 0.3),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:addCameraTrauma', [ctx.resolveInput('trauma')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:addCameraTrauma', ctx.getInput<number>('trauma'));
  },
};

export const CameraFade: NodeBehaviour = {
  id: 'camera.fade',
  name: 'Camera Fade',
  category: 'Camera',
  color: '#e91e63',
  icon: 'Sunset',
  description: 'Fades the camera to/from a color.',
  keywords: ['transition', 'black', 'white'],
  inputs: [
    flowIn(),
    input('color', 'Color', 'color', 0xFF000000),
    input('duration', 'Duration', 'f32', 1),
    input('fadeIn', 'Fade In', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:cameraFade', [
      ctx.resolveInput('color'),
      ctx.resolveInput('duration'),
      ctx.resolveInput('fadeIn'),
    ], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:cameraFade', ctx.getInput('color'), ctx.getInput<number>('duration'), ctx.getInput<boolean>('fadeIn'));
  },
};

// ============================================================================
// COORDINATE CONVERSION
// ============================================================================

export const ScreenToWorld: NodeBehaviour = {
  id: 'camera.screenToWorld',
  name: 'Screen To World',
  category: 'Camera',
  isPure: true,
  color: '#e91e63',
  icon: 'Maximize2',
  description: 'Converts screen coordinates to world coordinates.',
  keywords: ['position', 'convert', 'mouse', 'touch'],
  inputs: [
    input('x', 'Screen X', 'f32', 0),
    input('y', 'Screen Y', 'f32', 0),
  ],
  outputs: [
    output('pos', 'World Position', 'vec2'),
    output('wx', 'World X', 'f32'),
    output('wy', 'World Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:screenToWorld', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'vec2');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:screenToWorld', ctx.getInput<number>('x'), ctx.getInput<number>('y')) as { x: number; y: number } | null;
    ctx.setOutput('pos', r);
    ctx.setOutput('wx', r?.x ?? 0);
    ctx.setOutput('wy', r?.y ?? 0);
  },
};

export const WorldToScreen: NodeBehaviour = {
  id: 'camera.worldToScreen',
  name: 'World To Screen',
  category: 'Camera',
  isPure: true,
  color: '#e91e63',
  icon: 'Minimize2',
  description: 'Converts world coordinates to screen coordinates.',
  keywords: ['position', 'convert', 'ui'],
  inputs: [
    input('x', 'World X', 'f32', 0),
    input('y', 'World Y', 'f32', 0),
  ],
  outputs: [
    output('pos', 'Screen Position', 'vec2'),
    output('sx', 'Screen X', 'f32'),
    output('sy', 'Screen Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:worldToScreen', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'vec2');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:worldToScreen', ctx.getInput<number>('x'), ctx.getInput<number>('y')) as { x: number; y: number } | null;
    ctx.setOutput('pos', r);
    ctx.setOutput('sx', r?.x ?? 0);
    ctx.setOutput('sy', r?.y ?? 0);
  },
};

export const IsInView: NodeBehaviour = {
  id: 'camera.isInView',
  name: 'Is In View',
  category: 'Camera',
  isPure: true,
  color: '#e91e63',
  icon: 'Eye',
  description: 'Checks if a world position is visible in the camera.',
  keywords: ['visible', 'frustum', 'culling'],
  inputs: [
    input('x', 'World X', 'f32', 0),
    input('y', 'World Y', 'f32', 0),
  ],
  outputs: [
    output('visible', 'Is Visible', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isInView', [ctx.resolveInput('x'), ctx.resolveInput('y')], 'bool');
  },
  execute(ctx) {
    ctx.setOutput('visible', ctx.bridge.call('bridge:isInView', ctx.getInput<number>('x'), ctx.getInput<number>('y')) as boolean ?? false);
  },
};

export const GetViewportSize: NodeBehaviour = {
  id: 'camera.getViewportSize',
  name: 'Get Viewport Size',
  category: 'Camera',
  isPure: true,
  color: '#e91e63',
  icon: 'Monitor',
  description: 'Gets the current viewport size in world units.',
  keywords: ['screen', 'resolution', 'size'],
  inputs: [],
  outputs: [
    output('size', 'Size', 'vec2'),
    output('width', 'Width', 'f32'),
    output('height', 'Height', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getViewportSize', [], 'vec2');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:getViewportSize') as { x: number; y: number } | null;
    ctx.setOutput('size', r);
    ctx.setOutput('width', r?.x ?? 0);
    ctx.setOutput('height', r?.y ?? 0);
  },
};

export const CAMERA_NODES: readonly NodeBehaviour[] = [
  SetCameraPosition, GetCameraPosition,
  SetCameraTarget, ClearCameraTarget,
  SetCameraFollowSmoothing, SetCameraDeadzone, SetCameraLookahead,
  SetCameraZoom, GetCameraZoom, SetCameraZoomLimits,
  SetCameraBounds, ClearCameraBounds,
  CameraShake, AddCameraTrauma, CameraFade,
  ScreenToWorld, WorldToScreen, IsInView, GetViewportSize,
];
